/**
 * The WhatsApp tab on `/integrations` — the tenant's own Meta WhatsApp Business
 * account, and whether it can send right now.
 *
 * ## What changed in slice 16, and what did not
 *
 * The screen still exists for one failure mode above all others: **a system user
 * token that expires without saying so.** An expired token does not announce
 * itself — template submissions start failing, campaigns stop sending, and the
 * first symptom is usually a campaign that reached nobody. That has not changed,
 * and neither has the remedy: the expiry is the first thing on a connected
 * panel, with a date on it, shown whether it is good news or bad.
 *
 * What changed is where the panel sits and how it is arranged. Slice 7 put this
 * on the page as the third stacked card — under SES and, after slice 11, under
 * tracking, the redirect domain and VDM as well — so reaching the token warning
 * meant scrolling past every panel belonging to somebody else's channel. It is
 * now a tab.
 *
 * ## This file is now the state machine, and little else
 *
 * 704 lines became five files, split along the line the slice is about:
 *
 *   - `whatsapp-reading.ts`  — the verdict, derived once, with no request in it;
 *   - `WhatsAppSendState`    — the fold: everything that decides whether a
 *                              message can be sent;
 *   - `WhatsAppAdvanced`     — everything that does not, collapsed;
 *   - `WhatsAppConnectForm`  — the three fields and the one credential;
 *   - `WhatsAppDisconnect`   — the destructive action;
 *   - this file              — load, local state, the header badge, and the two
 *                              things the tab shell is allowed to touch.
 *
 * The split is not filing. The fold is a **module boundary**, so a control can
 * only cross it by being moved between two files — a diff somebody reads —
 * rather than by drifting down a thousand-line render, which is exactly how this
 * page reached the state slice 16 was called to fix.
 *
 * ## The contract with the tab shell, and the whole of it
 *
 * The one real cost of tabs is that a problem on the tab you are not looking at
 * is a problem you cannot see; stacked cards at least scrolled past it. So the
 * rail carries a status of its own, and it must be right for a tab nobody has
 * opened — an unmounted `TabPanel` is the case the dot exists for.
 *
 * The seam is one prop — `onStatusChange` — so the panel that loaded the row is
 * the only thing that judges it, and a token replaced *inside* the panel clears
 * the rail's dot without a second request. That prop and the badge in this
 * header are one `whatsAppTabStatus` call, so they cannot disagree, which on
 * this screen would mean a calm dot over an expired token.
 *
 * **A second export was here and has been removed.** `readWhatsAppTabStatus()`
 * fetched the row and derived the same status for a shell that unmounted its
 * panels and therefore had to ask for itself. It was written for an arrangement
 * that does not exist — `IntegrationTabRail` passes `keepMounted`, so this panel
 * loads with the page whichever tab is selected — and it never had a caller. An
 * exported function that makes a network request and is called by nothing is not
 * an option kept ready; it is a second, untested path to the same answer, and
 * the day somebody reaches for it they will be reaching for code no verify
 * script has ever run. The same argument this file already made two paragraphs
 * down about a compile-bridge alias. A shell that ever does unmount its panels
 * has the pieces in the open: `fetchWhatsAppIntegration`, then
 * `readWhatsAppIntegration` and `whatsAppTabStatus`, both pure and both covered.
 *
 * ## Two things the old card could not see
 *
 * It consulted `readiness` **only when this session had pressed Test**, so an
 * unverified number or a red quality rating was invisible on a first paint. And
 * it reimplemented `metaTokenExpiry` privately, fourteen-day threshold and all.
 * Both are gone into `whatsapp-reading.ts`, which calls `@wend/shared`'s own
 * functions — the API sets the row's status and gates campaign scheduling with
 * those, and a browser that works the verdict out for itself is a browser that
 * can tell somebody they are ready to send while the scheduler refuses them.
 *
 * ## The file is still called `WhatsAppCard.tsx`, and the export is not
 *
 * The component is `WhatsAppTabPanel`, which is what it is. The filename stayed
 * because the email half of slice 16 was written in parallel against this path
 * and renaming it mid-slice would have broken a file two people were editing;
 * `IntegrationsPage.tsx` imports `WhatsAppTabPanel` from `./WhatsAppCard`, which
 * reads oddly and costs nothing. It is a rename to make on its own, in a commit
 * that does nothing else — a name change buried in a reorganisation is a diff
 * nobody can read.
 *
 * A `WhatsAppCard` alias was exported here for one revision as a compile bridge
 * while the shell landed. It is gone: the shell imports the real name, and an
 * alias nothing imports is a second name for one thing, which is how a codebase
 * ends up with two.
 *
 * ## SECURITY
 *
 * No credential is handled in this file. The system user token lives in
 * `WhatsAppConnectForm` for the length of one submission, and that file's header
 * is where the rule is stated. Nothing here renders it, stores it, or has a
 * response shape it could arrive in.
 */
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Kicker,
  Spinner,
} from '@wend/ui';
import { KeyRound, MessageCircle, RotateCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { ErrorState } from '../../components/ErrorState';
import {
  checkWhatsApp,
  fetchWhatsAppIntegration,
  type WhatsAppCheckResponse,
  type WhatsAppIntegration,
} from '../../lib/api/whatsapp';
import { useAsyncData } from '../../lib/use-async-data';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';
import { WhatsAppAdvanced } from './WhatsAppAdvanced';
import { WhatsAppConnectForm } from './WhatsAppConnectForm';
import { WhatsAppDisconnect } from './WhatsAppDisconnect';
import { WhatsAppSendState } from './WhatsAppSendState';
import {
  WHATSAPP_TAB_ATTENTION_TONE,
  readWhatsAppIntegration,
  whatsAppTabStatus,
  type WhatsAppSessionCheck,
  type WhatsAppTabStatus,
} from './whatsapp-reading';

export interface WhatsAppTabPanelProps {
  /**
   * Called whenever this panel learns a status the rail has not heard yet.
   *
   * It fires only once the panel actually knows — never during the first load
   * and never after a request that failed, because "nothing connected" is a
   * claim and a request that did not answer supports no claim at all. A dot
   * cleared by a request that never came back is worse than no dot.
   */
  readonly onStatusChange?: (status: WhatsAppTabStatus) => void;
}

export function WhatsAppTabPanel({ onStatusChange }: WhatsAppTabPanelProps) {
  /**
   * A wrapper object rather than `WhatsAppIntegration | null`, so that "not
   * loaded yet" and "loaded, nothing connected" stay different values. Both are
   * null on the inside, and conflating them shows the connect form to somebody
   * whose request actually failed.
   */
  const { data, error, loadingFirst, refreshing, reload } = useAsyncData(
    async (signal) => ({ integration: await fetchWhatsAppIntegration(signal) }),
    [],
  );

  const [local, setLocal] = useState<{ integration: WhatsAppIntegration } | null>(null);
  const [sessionCheck, setSessionCheck] = useState<WhatsAppSessionCheck | null>(null);
  const [replacing, setReplacing] = useState(false);

  const integration = local?.integration ?? data?.integration ?? null;
  const known = local !== null || data !== null;
  const reading = readWhatsAppIntegration(integration, sessionCheck);
  const status = whatsAppTabStatus(reading);

  useStatusReport(known ? status : null, onStatusChange);

  function onAnswered(response: WhatsAppCheckResponse): void {
    setLocal({ integration: response.integration });
    setSessionCheck({ outcome: response.outcome, readiness: response.readiness });
    setReplacing(false);
  }

  if (!known) {
    return (
      <Card>
        {loadingFirst ? (
          <CardContent className="flex items-center gap-3 px-6 py-10 text-[0.92rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Loading your WhatsApp connection…</span>
          </CardContent>
        ) : (
          <ErrorState
            error={error}
            title="Could not load your WhatsApp connection"
            onRetry={reload}
          />
        )}
      </Card>
    );
  }

  const replaceButton = (label: string) => (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={() => {
        setReplacing(true);
      }}
    >
      <KeyRound aria-hidden="true" className="size-4" />
      {label}
    </Button>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <span className="grid size-8 shrink-0 place-items-center rounded-base border border-line bg-white/[0.04] text-muted">
            <MessageCircle aria-hidden="true" className="size-4" />
          </span>
          <Kicker as="span">
            WhatsApp · Meta
          </Kicker>
          {/* The rail's dot and this badge are the same value. See the header. */}
          <Badge tone={WHATSAPP_TAB_ATTENTION_TONE[status.attention]}>{status.label}</Badge>
          {refreshing && <Spinner size="sm" label="Refreshing" />}
        </div>
        <CardTitle>WhatsApp Business</CardTitle>
        {/*
          A description only where there is nothing else to say. The connected
          one used to explain that every figure came from Meta rather than from
          documentation — true, and now said where it is checkable: on the
          summary strip, beside the moment they were read.
        */}
        {integration === null && (
          <CardDescription>
            Connect the Meta WhatsApp Business account this workspace sends from. The number, the
            quality rating and Meta’s bill stay yours.
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        {integration === null || replacing ? (
          <WhatsAppConnectForm
            existing={integration}
            onConnected={onAnswered}
            {...(replacing
              ? {
                  onCancel: () => {
                    setReplacing(false);
                  },
                }
              : {})}
          />
        ) : (
          <div className="flex flex-col gap-6">
            <WhatsAppSendState
              integration={integration}
              reading={reading}
              sessionCheck={sessionCheck}
              replaceToken={replaceButton('Replace token')}
            />

            <div className="flex flex-wrap items-center gap-3">
              <TestButton onChecked={onAnswered} />
              {replaceButton('Replace credentials')}
            </div>

            <WhatsAppAdvanced
              integration={integration}
              reading={reading}
              disconnect={
                <WhatsAppDisconnect
                  integration={integration}
                  onDisconnected={() => {
                    setLocal(null);
                    setSessionCheck(null);
                    reload();
                  }}
                />
              }
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Reports a status upward, once per distinct status.
 *
 * Deliberately no dependency array — the same shape `TabList` in `@wend/ui` uses,
 * and for the same reason: it early-returns the moment there is nothing new, and
 * what it compares is a value the caller recreates on every render. An array over
 * `status` would fire every render; one over `onStatusChange` would go stale the
 * moment the shell passed an inline arrow.
 *
 * `null` means "this panel does not know yet", and nothing is reported for it.
 */
function useStatusReport(
  status: WhatsAppTabStatus | null,
  onStatusChange: ((status: WhatsAppTabStatus) => void) | undefined,
): void {
  const reported = useRef<string | null>(null);

  useEffect(() => {
    if (status === null || onStatusChange === undefined) return;
    const key = `${status.attention} ${status.label}`;
    if (reported.current === key) return;
    reported.current = key;
    onStatusChange(status);
  });
}

function TestButton({ onChecked }: { onChecked: (response: WhatsAppCheckResponse) => void }) {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);

  async function run(): Promise<void> {
    if (pending) return;
    setFailure(null);
    setPending(true);
    try {
      onChecked(await checkWhatsApp());
    } catch (error) {
      setFailure(requestFailure(error));
    }
    setPending(false);
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        loading={pending}
        onClick={() => {
          void run();
        }}
      >
        <RotateCw aria-hidden="true" className="size-4" />
        {pending ? 'Asking Meta' : 'Test connection'}
      </Button>
      {failure !== null && <RequestFailureAlert failure={failure} />}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Request failures                                                            */
/* -------------------------------------------------------------------------- */

/*
 * `RequestFailure`, `requestFailure` and `RequestFailureAlert` moved to
 * `./request-failure`, unchanged, when slice 11 added a third card to this
 * screen that needs them. The distinction they draw is the one this panel draws
 * against Meta and the SES panel draws against AWS: a refused token is an answer
 * to render, this is not having got an answer at all.
 */
